/**
 * Format a monetary amount with the correct currency symbol and locale formatting.
 *
 * Uses Intl.NumberFormat which handles currency symbols ($, EUR, GBP, etc.),
 * decimal separators, and symbol placement.
 *
 * @param amount - The numeric amount to format
 * @param currencyCode - ISO 4217 currency code (e.g., "USD", "EUR"). Defaults to "USD".
 * @returns Formatted currency string (e.g., "$1,234.56", "€1,234.56")
 */
export function formatCurrency(amount: number, currencyCode?: string): string {
  const code = (currencyCode || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString()} ${code}`;
  }
}

/**
 * Like `formatCurrency`, but drops a trailing `.00`.
 *
 * For a dense table of deal amounts, ".00" repeated down every row is width
 * spent on nothing: it is only ever informative when the cents are non-zero,
 * and those keep their decimals here. "$9,000" and "$5,760.50" both read
 * correctly, and the column gets narrower.
 *
 * Deliberately not the default: an invoice total or a line item should keep
 * its exact decimals, since a figure being reconciled against Stripe is a
 * different job from a figure being scanned in a list.
 *
 * Zero-decimal currencies (JPY, KRW, …) are unaffected: their amounts are
 * always integers, so they take the no-decimals branch and render exactly as
 * Intl's own default would.
 */
export function formatCurrencyCompact(amount: number, currencyCode?: string): string {
  const code = (currencyCode || 'USD').toUpperCase();
  try {
    // Only suppress decimals when there are none to lose. A fractional amount
    // keeps the currency's normal precision.
    const whole = Number.isInteger(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      ...(whole ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {}),
    }).format(amount);
  } catch {
    return `${amount.toLocaleString()} ${code}`;
  }
}
